import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { Activity } from '~/core/model/activity.model';
import { CartService } from '~/checkout/services/cart/cart.service';
import { CheckoutItem } from '~/checkout/model/checkout-item.model';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';
import { ModalComponent } from '~/component/modal/modal.component';
import { OrganizationInterface } from '@sport-activities/sdk';
import { OrganizationService } from '~/core/services/organization/organization.service';
import { Timeslot } from '~/core/model/timeslot.model';
import { TimeslotListVariantEnum } from '~/component/activity-timeslot-list/enums/timeslot-list-variant.enum';
import { ParamethorNameEnum } from '~/paramethor/names.enum';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { UserService } from '~/auth/services/user/user.service';
import { AuthService } from '~/auth/services/auth/auth.service';
import { CONNECT_ISSUER } from '~/auth/providers/connect.provider';
import { WindowService } from '~/core/services/window/window.service';
import { isPlatformBrowser } from '@angular/common';
import { BookingService } from '~/checkout/services/booking/booking.service';
import { appConfig } from '~/app.config';

@Component({
  selector: 'app-attendee-activity-timeslot-list',
  templateUrl: './activity-timeslot-list.component.html',
  styleUrls: ['./activity-timeslot-list.component.scss'],
})
export class AttendeeActivityTimeslotListComponent
  implements OnInit, OnDestroy
{
  @ViewChild('confirmNewCartModal') public confirmNewCartModal: ModalComponent;
  @ViewChild('cartIsFullModal') public cartIsFullModal: ModalComponent;

  @Input() public activity: Activity;
  @Input() public hasLoadMoreButton = false;
  @Input() public hasPagination = false;
  @Input() public hasSeeMoreUrl = false;
  @Input() public timeslots: Timeslot[] = [];

  @Output() public loadMore = new EventEmitter<void>();
  @Output() public selectTimeslot = new EventEmitter<Timeslot>();
  @Output() public cancelTimeslot = new EventEmitter<Timeslot>();

  @Output() public login = new EventEmitter<void>();

  private readonly subscriptions = new Subscription();
  public organizer: OrganizationInterface;

  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly timeslotVariant = TimeslotListVariantEnum.EXTENDED;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly ctaButtonType = CtaButtonTypeEnum;

  public cartItems: CheckoutItem[];
  public modalTranslation: string;
  public isRedirect: boolean;

  constructor(
    @Inject(CONNECT_ISSUER) private readonly connectIssuer: string,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    private readonly cartService: CartService,
    private readonly checkoutService: CheckoutService,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly organizationService: OrganizationService,
    private readonly paramethorService: ParamethorService,
    private readonly translateService: TranslateService,
    private readonly userService: UserService,
    private readonly authService: AuthService,
    private readonly windowService: WindowService,
    private readonly bookingService: BookingService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.redirectionPartnerHandler();

    this.subscriptions.add(
      this.organizationService
        .getOrganizationById(this.activity?.organizationIdentifier)
        .subscribe((organization) => {
          this.organizer = organization;
        })
    );

    this.subscriptions.add(
      this.checkoutService.getCartItems().subscribe((cartItems) => {
        this.cartItems = cartItems;
      })
    );
  }

  private redirectionPartnerHandler(): void {
    if (isPlatformBrowser(this.platformId)) {
      if (localStorage.getItem('redirectToPartner')) {
        this.windowService.openNewTab(
          localStorage.getItem('redirectToPartner')
        );
        localStorage.removeItem('redirectToPartner');
      }
    }
  }

  public get organizerName(): string {
    return this.organizer?.name;
  }

  public get seeMoreButton(): boolean {
    if (!this.hasSeeMoreUrl || (this.timeslots?.length ?? 0) === 0) {
      return false;
    }

    // if no timeslots are bookable and less than numberToDisplay are shown, there is nothing more to see
    // otherwise, show the button
    return !(
      this.timeslots?.every((t) => !t.isBookable()) &&
      this.timeslots.length < appConfig.numberOfTimeslotsToDisplay
    );
  }

  public get seeMoreUrl(): string {
    const base = this.activity?.isLive
      ? 'attendee.show-live-activity-dates'
      : 'attendee.show-activity-dates';

    return this.localizeRouterService
      .translateRoute(`/${base}/${this.activity?.sku}`)
      .toString();
  }

  public get newCartModalText(): string {
    return this.translateService.instant('checkout.cart.alert.new.text', {
      organizer: this.organizer?.name,
    });
  }

  public get cartIsFullTextTitle(): string {
    return this.translateService.instant('checkout.cart.full.title');
  }

  public get cartIsFullText(): string {
    return this.translateService.instant('checkout.cart.full');
  }

  public get goToCartText(): string {
    return this.translateService.instant('checkout.cart.full.see_cart');
  }

  public get closeModalText(): string {
    return this.translateService.instant('checkout.cart.full.close');
  }

  public cartIsFull(cartTotalItems: number): boolean {
    const maxItemsInCart = this.activity?.isBms ? 1 : 3;
    return cartTotalItems >= maxItemsInCart;
  }

  public isTimeslotInCart(timeslot): boolean {
    this.checkoutService.getCartContent();
    return this.checkoutService.cartHasItem(timeslot);
  }

  public getCtaAddText(): string {
    return this.translateService.instant('checkout.cart.add_short');
  }

  public getCtaDeleteText(): string {
    return this.translateService.instant('checkout.cart.delete.confirm');
  }

  public deleteTimeslot(timeslot: Timeslot): void {
    if (this.activity?.isBms && this.isFree) {
      const bookingId = localStorage.getItem('bookingId');
      if (bookingId) {
        this.bookingService.deleteOneFFBooking(bookingId).subscribe();
      }
    }

    this.cancelTimeslot.emit(timeslot);
  }

  public get isFree(): boolean {
    return this.activity?.minPriceOffer?.price === 0;
  }

  public continueWithCart(timeslot): void {
    if (
      this.cartItems &&
      this.cartItems.length > 0 &&
      this.cartItems.some(
        (cartItem) =>
          cartItem.timeslot.activityIdentifier !==
            timeslot.activityIdentifier || cartItem.activity.isBms
      )
    ) {
      this.confirmNewCartModal.show();
      return;
    }

    if (
      this.cartIsFull(this.cartItems === undefined ? 0 : this.cartItems.length)
    ) {
      this.cartIsFullModal.show();
      return;
    }
    this.selectTimeslot.emit(timeslot);
  }

  public addToCart(timeslot): void {
    this.continueWithCart(timeslot);
  }

  public redirectBookingUrl(metaBooking: string): void {
    if (
      !this.paramethorService.isTrue(
        ParamethorNameEnum.LoginBeforeExternalBooking
      )
    ) {
      this.windowService.openNewTab(metaBooking);
      return;
    }

    localStorage.setItem('redirectToPartner', metaBooking);
    this.userService.isAuthenticated().subscribe((isLoggedIn) => {
      if (isLoggedIn) {
        this.redirectionPartnerHandler();
      } else {
        localStorage.removeItem('redirectToPartner');
        localStorage.setItem('fromActivity', this.activity?.sku?.toString());
        const urlToSend = encodeURIComponent(metaBooking.toString());

        this.authService.login(this.connectIssuer, {
          from: this.localizeRouterService
            .translateRoute(`/attendee.external-url/${urlToSend}`)
            .toString(),
          lang: this.translateService.currentLang,
        });
      }
    });
  }

  public closeConfirmNewCartModal(): void {
    this.confirmNewCartModal.hide();
  }

  public newCart(timeslot): void {
    this.checkoutService.emptyCart();
    this.closeConfirmNewCartModal();
    this.selectTimeslot.emit(timeslot);
  }

  public closeCartIsFullModal(): void {
    this.cartIsFullModal.hide();
  }

  public openCart(): void {
    this.closeCartIsFullModal();
    this.cartService.openCart();
  }
}
